import { type Role, Roles, RolesMatch, type RolesMatchType } from '@trinity/blockchain-management'

export const RoutePaths = {
    Home: '/',
    Portfolio: '/portfolio',
    GetUTY: '/get-uty',
    UseUTY: '/use-uty',
    Documentation: '/documentation',
    TermsOfUse: '/terms-of-use',
    PrivacyPolicy: '/privacy-policy',
    MintUTY: '/mint-uty',
    AdminHome: '/admin',
    AdminDashboard: '/admin/dashboard',
    AdminDeposits: '/admin/deposits',
    AdminRedemptions: '/admin/redemptions',
    AdminWhitelist: '/admin/whitelist',
    AdminRoles: '/admin/roles',
    AdminConfiguration: '/admin/configuration',
    AdminFunds: '/admin/funds',
} as const

export type RoutePath = (typeof RoutePaths)[keyof typeof RoutePaths]

export interface Route {
    path: RoutePath
    label: string
    requiresRoles?: { roles: Role[]; match: RolesMatchType }
}

export const routes: Record<RoutePath, Route> = {
    [RoutePaths.Home]: { path: RoutePaths.Home, label: 'Home' },
    [RoutePaths.Portfolio]: { path: RoutePaths.Portfolio, label: 'Portfolio' },
    [RoutePaths.GetUTY]: { path: RoutePaths.GetUTY, label: 'Get UTY' },
    [RoutePaths.UseUTY]: { path: RoutePaths.UseUTY, label: 'Use UTY' },
    [RoutePaths.Documentation]: { path: RoutePaths.Documentation, label: 'Documentation' },
    [RoutePaths.TermsOfUse]: { path: RoutePaths.TermsOfUse, label: 'Terms of Use' },
    [RoutePaths.PrivacyPolicy]: { path: RoutePaths.PrivacyPolicy, label: 'Privacy Policy' },
    [RoutePaths.AdminHome]: { path: RoutePaths.AdminHome, label: 'Admin' },
    [RoutePaths.MintUTY]: {
        path: RoutePaths.MintUTY,
        label: 'Mint UTY',
        requiresRoles: { roles: [Roles.Whitelisted], match: RolesMatch.All },
    },
    [RoutePaths.AdminDashboard]: {
        path: RoutePaths.AdminDashboard,
        label: 'Dashboard',
        requiresRoles: { roles: [Roles.Admin, Roles.Operator, Roles.DefaultAdmin], match: RolesMatch.OneOf },
    },
    [RoutePaths.AdminDeposits]: {
        path: RoutePaths.AdminDeposits,
        label: 'Deposits',
        requiresRoles: { roles: [Roles.Admin, Roles.Operator], match: RolesMatch.OneOf },
    },
    [RoutePaths.AdminRedemptions]: {
        path: RoutePaths.AdminRedemptions,
        label: 'Redemptions',
        requiresRoles: { roles: [Roles.Admin, Roles.Operator], match: RolesMatch.OneOf },
    },
    [RoutePaths.AdminWhitelist]: {
        path: RoutePaths.AdminWhitelist,
        label: 'Whitelist',
        requiresRoles: { roles: [Roles.DefaultAdmin], match: RolesMatch.OneOf },
    },
    [RoutePaths.AdminRoles]: {
        path: RoutePaths.AdminRoles,
        label: 'Roles',
        requiresRoles: { roles: [Roles.DefaultAdmin], match: RolesMatch.OneOf },
    },
    [RoutePaths.AdminConfiguration]: {
        path: RoutePaths.AdminConfiguration,
        label: 'Configuration',
        requiresRoles: { roles: [Roles.DefaultAdmin], match: RolesMatch.OneOf },
    },
    [RoutePaths.AdminFunds]: {
        path: RoutePaths.AdminFunds,
        label: 'Funds',
        requiresRoles: { roles: [Roles.Admin, Roles.Operator], match: RolesMatch.OneOf },
    },
}

export const allRoutes: Route[] = Object.values(routes)

export const userRoutes: Route[] = [
    routes[RoutePaths.Portfolio],
    routes[RoutePaths.GetUTY],
    routes[RoutePaths.UseUTY],
    routes[RoutePaths.MintUTY],
]

export const infoRoutes: Route[] = [
    routes[RoutePaths.Documentation],
    routes[RoutePaths.TermsOfUse],
    routes[RoutePaths.PrivacyPolicy],
]

export const adminRoutes: Route[] = [
    routes[RoutePaths.AdminDashboard],
    routes[RoutePaths.AdminDeposits],
    routes[RoutePaths.AdminRedemptions],
    routes[RoutePaths.AdminWhitelist],
    routes[RoutePaths.AdminRoles],
    routes[RoutePaths.AdminConfiguration],
    routes[RoutePaths.AdminFunds],
]

export type InfoRoute = (typeof infoRoutes)[number]['path']

interface SocialMediaLink {
    key: SocialMediaLinkKey
    label: string
    url: string
}

export type SocialMediaLinkKey = 'telegram' | 'x'

export const socialMediaLinks: SocialMediaLink[] = [
    { key: 'telegram', label: 'Telegram', url: 'https://t.me/uty_finance' },
    { key: 'x', label: 'X', url: 'https://x.com/xsy_fi' },
]
